/**
 * DESIGN SYSTEM - ANIMATION UTILITIES
 * Shared Framer Motion variants and configuration
 */

// =============================================================================
// EASING CURVES
// =============================================================================

export const easings = {
  // "Expo out" style - smooth deceleration
  smooth: [0.22, 1, 0.36, 1],
  // Standard ease out
  easeOut: [0, 0, 0.2, 1],
  // Bounce
  bounce: [0.68, -0.55, 0.265, 1.55],
  // Spring-like
  spring: { type: 'spring', stiffness: 400, damping: 25 },
};

// =============================================================================
// TIMING
// =============================================================================

export const durations = {
  fast: 0.2,
  normal: 0.3,
  medium: 0.5,
  slow: 0.6,
  verySlow: 0.8,
};

// =============================================================================
// VIEWPORT TRIGGER OPTIONS
// =============================================================================

export const viewportOptions = {
  // Standard - trigger when 100px into viewport
  standard: { once: true, margin: '-100px' },
  // Shallow - trigger when 50px into viewport
  shallow: { once: true, margin: '-50px' },
  // Immediate - trigger as soon as visible
  immediate: { once: true },
  // Repeat - trigger every time
  repeat: { once: false, margin: '-100px' },
};

// =============================================================================
// CONTAINER VARIANTS
// =============================================================================

/**
 * Stagger container - wraps children that should animate in sequence
 * Usage: <motion.div variants={staggerContainer} initial="hidden" animate="visible">
 */
export const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

/**
 * Fast stagger - for lists with many items
 */
export const staggerContainerFast = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
};

// =============================================================================
// ITEM VARIANTS
// =============================================================================

/**
 * Fade in and slide up - most common animation
 */
export const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: durations.slow, ease: easings.smooth },
  },
};

/**
 * Fade in and slide down
 */
export const fadeInDown = {
  hidden: { opacity: 0, y: -30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: durations.slow, ease: easings.smooth },
  },
};

/**
 * Fade in and slide from left
 */
export const fadeInLeft = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: durations.slow, ease: easings.smooth },
  },
};

/**
 * Fade in and slide from right
 */
export const fadeInRight = {
  hidden: { opacity: 0, x: 30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: durations.slow, ease: easings.smooth },
  },
};

/**
 * Scale in - for cards, modals, popups
 */
export const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: durations.medium, ease: easings.smooth },
  },
};

/**
 * Scale in from center - for icons, badges
 */
export const scaleInCenter = {
  hidden: { opacity: 0, scale: 0.5 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: durations.normal, ease: easings.smooth },
  },
};

/**
 * Simple fade
 */
export const fadeIn = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: durations.medium },
  },
};

// =============================================================================
// SPECIAL EFFECTS
// =============================================================================

/**
 * Floating animation for decorative elements
 * Usage: <motion.div animate={floatingAnimation} transition={floatingTransition} />
 */
export const floatingAnimation = {
  x: [0, 20, 0],
  y: [0, -15, 0],
};

export const floatingTransition = {
  duration: 8,
  repeat: Infinity,
  ease: 'easeInOut',
};

/**
 * Alternative floating (opposite direction)
 */
export const floatingAnimationAlt = {
  x: [0, -15, 0],
  y: [0, 20, 0],
};

export const floatingTransitionAlt = {
  duration: 10,
  repeat: Infinity,
  ease: 'easeInOut',
};

/**
 * Underline reveal animation
 * Usage: <motion.span initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={underlineTransition} style={{ originX: 0 }} />
 */
export const underlineTransition = {
  delay: 0.8,
  duration: durations.slow,
  ease: easings.smooth,
};

// =============================================================================
// INTERACTIVE STATES
// =============================================================================

/**
 * Button hover/tap effects
 * Usage: <motion.button whileHover={buttonHover} whileTap={buttonTap} />
 */
export const buttonHover = { scale: 1.02 };
export const buttonTap = { scale: 0.98 };

/**
 * Card hover effect
 */
export const cardHover = { y: -4, transition: { duration: durations.fast } };

/**
 * Icon bounce on hover
 */
export const iconHover = { scale: 1.1, transition: easings.spring };

// =============================================================================
// DRAG STATES (for upload zones, etc.)
// =============================================================================

export const dragActive = { scale: 1.02, y: -5 };
export const dragInactive = { scale: 1, y: 0 };
export const dragTransition = easings.spring;

// =============================================================================
// PAGE TRANSITIONS
// =============================================================================

export const pageTransition = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: durations.normal, ease: easings.smooth },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Create a custom stagger container with specific timing
 * @param {number} staggerDelay - Time between each child animation
 * @param {number} initialDelay - Delay before first child animates
 */
export function createStaggerContainer(staggerDelay = 0.1, initialDelay = 0.2) {
  return {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: initialDelay,
      },
    },
  };
}

/**
 * Create a fade-in variant with custom direction and distance
 * @param {'up' | 'down' | 'left' | 'right'} direction
 * @param {number} distance - Pixels to travel
 */
export function createFadeIn(direction = 'up', distance = 30) {
  const axis = direction === 'up' || direction === 'down' ? 'y' : 'x';
  const value = direction === 'up' || direction === 'left' ? distance : -distance;

  return {
    hidden: { opacity: 0, [axis]: value },
    visible: {
      opacity: 1,
      [axis]: 0,
      transition: { duration: durations.slow, ease: easings.smooth },
    },
  };
}
